/**
 * ProviderQueue — TV-P1-02 / TV-RV-08
 *
 * TV-RV-08: role-aware queue mode.
 *   - provider  → original "Start Visit" flow (creates FHIR Encounter, navigates to /visit)
 *   - front-desk → operational actions only: Check-In / Mark Arrived / No-Show /
 *                  Assign-to-Provider.  The "Start Visit" button and all clinical
 *                  info (conditions, labs, A1c) are hidden (`cfg.canSeeClinical = false`).
 *
 * Both roles see the same queue list, stats row, and AI Pre-Visit Brief. The
 * difference is entirely in PatientPreviewCard.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  AlertCircle,
  CheckCircle,
  Heart,
  Loader2,
  RefreshCw,
  UserCheck,
  UserX,
  Users,
  LogIn,
} from 'lucide-react';
import { Card, Badge, Avatar, StatCard, AIPanel } from '@/components';
import { useSessionStore } from '@/stores/sessionStore';
import {
  useTodaysAppointments,
  usePatientSummary,
  useCreateEncounter,
  useCareGaps,
} from '@/fhir/hooks';
import {
  mapAppointment,
  mapConditions,
  mapLabs,
} from '@/fhir/mappers';
import { FhirErrorState, FhirErrorBanner } from '@/fhir/errors';
import { useAuth } from '@/auth/AuthProvider';
import { useTenantQueue } from '@/firebase/hooks';
import { getViewConfig } from '@/auth/viewConfig';

/**
 * ProviderQueue — TV-P1-02
 *
 * Pulls today's Appointments for the logged-in practitioner from FHIR
 * and renders them as the visit queue. Clicking a row lazy-loads that
 * patient's summary (problems, labs) for the right-side preview pane.
 * No mock data; every list is FHIR-backed.
 *
 * Buckets:
 *   - waiting    → status booked
 *   - in-visit   → status arrived | checked-in
 *   - completed  → status fulfilled
 * (Cancelled / no-show / entered-in-error are filtered out at the
 * search level, status= clause.)
 */

const TriageDot = ({ level }) => {
  const colors = {
    red: 'bg-red-500',
    amber: 'bg-amber-500',
    green: 'bg-green-500',
    blue: 'bg-blue-500',
  };
  return <div className={`w-3 h-3 rounded-full ${colors[level] || colors.blue}`} />;
};

export default function ProviderQueue() {
  const navigate = useNavigate();
  const { user, smartContext } = useAuth();
  const cfg = getViewConfig(user?.role ?? 'patient');

  const sessionFhirUser = useSessionStore((s) => s.smartContext?.fhirUser);
  const tenantId = useSessionStore((s) => s.tenantId);
  const setCurrentEncounter = useSessionStore((s) => s.setCurrentEncounter);
  const setCurrentPatient = useSessionStore((s) => s.setCurrentPatient);
  const createEncounter = useCreateEncounter();
  // Holds the raw error (with .status) so FhirErrorBanner can render it.
  const [startVisitError, setStartVisitError] = useState(null);

  // Practitioner id sources, in order:
  //   1. AuthProvider's user.fhirUser ('Practitioner/123')
  //   2. SMART context.fhirUser
  //   3. Session store mirror
  const practitionerRef =
    user?.fhirUser || smartContext?.fhirUser || sessionFhirUser || null;

  const {
    data: appointmentBundle,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useTodaysAppointments(practitionerRef);

  // TV-P2-02: realtime overlay. Patients currently in the waiting room
  // write a doc to televisit-queue/{tenantId}/patients/{patientId}; we
  // listen to that collection and merge their state into the
  // FHIR-Appointment-derived queue. Provider sees arrivals + intake
  // progress live without a refresh.
  const {
    rows: liveQueueRows,
    error: liveQueueError,
  } = useTenantQueue(tenantId);
  const liveByPatient = useMemo(() => {
    const m = new Map();
    for (const r of liveQueueRows || []) {
      if (r.patientId) m.set(r.patientId, r);
    }
    return m;
  }, [liveQueueRows]);

  // Shape each Appointment into the row shape the UI consumes.
  // When the same patient has a live waiting-room doc, splice in
  // their realtime intakeProgress + queue status so the row reflects
  // current presence.
  const queue = useMemo(() => {
    if (!appointmentBundle?.appointments) return [];
    return appointmentBundle.appointments
      .map((e) => mapAppointment(e.resource, appointmentBundle.patients))
      .filter(Boolean)
      .map((row) => {
        const live = row.patientId ? liveByPatient.get(row.patientId) : null;
        if (!live) return row;
        // Live presence wins for queue bucket + intake progress; FHIR
        // remains the source of truth for chief complaint, etc.
        return {
          ...row,
          live: true,
          intakeProgress: live.intakeProgress || row.intakeProgress,
          // If the patient actually arrived (status=waiting), reflect
          // that — overrides FHIR's 'booked' default. Only flip the
          // queueBucket forward, never backward.
          queueBucket:
            live.status === 'in-visit'
              ? 'in-visit'
              : live.status === 'waiting' && row.queueBucket === 'waiting'
                ? 'waiting'
                : row.queueBucket,
        };
      });
  }, [appointmentBundle, liveByPatient]);

  const [selectedId, setSelectedId] = useState(null);

  // Default selection: first waiting row, else first row.
  const effectiveSelectedId =
    selectedId
    ?? queue.find((q) => q.queueBucket === 'waiting')?.id
    ?? queue[0]?.id
    ?? null;

  const selectedRow = queue.find((q) => q.id === effectiveSelectedId) || null;

  // Lazy-load the selected patient's summary for the right preview pane.
  const {
    data: selectedSummary,
    isLoading: isSummaryLoading,
    error: summaryError,
  } = usePatientSummary(selectedRow?.patientId);

  const selectedConditions = useMemo(
    () => (selectedSummary ? mapConditions(selectedSummary.conditions) : []),
    [selectedSummary],
  );
  const selectedLabs = useMemo(
    () => (selectedSummary ? mapLabs(selectedSummary.labs) : []),
    [selectedSummary],
  );

  // TV-P1-05: surface the top gaps in the pre-visit brief.
  const {
    data: careGapData,
    isLoading: isGapsLoading,
  } = useCareGaps(selectedRow?.patientId);
  const careGaps = careGapData?.gaps || [];

  // Buckets for the stat row.
  const stats = useMemo(() => {
    const waiting = queue.filter((q) => q.queueBucket === 'waiting');
    const completed = queue.filter((q) => q.queueBucket === 'completed');
    const inVisit = queue.filter((q) => q.queueBucket === 'in-visit');
    const billable = completed.length;
    const avgWait = waiting.length
      ? Math.round(waiting.reduce((s, q) => s + (q.waitTime || 0), 0) / waiting.length)
      : 0;
    return {
      inQueue: waiting.length + inVisit.length,
      completed: completed.length,
      avgWait,
      billableToday: billable,
    };
  }, [queue]);

  const handleStartVisit = async (row) => {
    if (!row || createEncounter.isPending) return;
    setStartVisitError(null);
    try {
      const encounter = await createEncounter.mutateAsync({
        patientId: row.patientId,
        practitionerId: practitionerRef,
        appointmentId: row.appointmentId,
        chiefComplaint: row.chiefComplaint,
      });

      // Persist the new context so LiveVisit + PatientPanel pick it up.
      setCurrentEncounter(encounter.id);
      if (row.patientId) setCurrentPatient(row.patientId);

      navigate(`/visit/${encounter.id}`);
    } catch (err) {
      console.error('[ProviderQueue] Start Visit failed:', err);
      setStartVisitError(err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">
            {cfg.canSeeClinical ? 'Provider Queue' : 'Front-Desk Queue'}
          </h1>
          <p className="text-slate-400">
            {practitionerRef
              ? "Today's scheduled visits"
              : 'Waiting for practitioner context from the SMART launch.'}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-2 bg-slate-900 border border-slate-800 hover:border-slate-700 text-slate-300 text-sm rounded-lg disabled:opacity-50"
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard label="In Queue" value={stats.inQueue} icon={<Heart className="w-5 h-5" />} color="blue" />
        <StatCard label="Completed" value={stats.completed} icon={<CheckCircle className="w-5 h-5" />} color="green" />
        <StatCard label="Avg Wait (min)" value={stats.avgWait} icon={<Clock className="w-5 h-5" />} color="amber" />
        <StatCard label="Billable Today" value={stats.billableToday} icon={<AlertCircle className="w-5 h-5" />} color="purple" />
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left: Patient Queue List */}
        <div className="col-span-1">
          <Card className="bg-slate-900 border-slate-800 p-0">
            <div className="p-4 border-b border-slate-800">
              <h2 className="text-lg font-semibold text-white">Queue</h2>
            </div>
            <QueueList
              isLoading={isLoading}
              error={error}
              practitionerRef={practitionerRef}
              queue={queue}
              selectedId={effectiveSelectedId}
              onSelect={setSelectedId}
              onRetry={() => refetch()}
            />
          </Card>
        </div>

        {/* Right: Patient Preview + AI Panel */}
        <div className="col-span-2 space-y-6">
          {selectedRow ? (
            <PatientPreviewCard
              row={selectedRow}
              conditions={selectedConditions}
              labs={selectedLabs}
              isLoading={isSummaryLoading}
              error={summaryError}
              onStart={() => handleStartVisit(selectedRow)}
              startPending={createEncounter.isPending}
              startError={startVisitError}
              canSeeClinical={cfg.canSeeClinical}
            />
          ) : (
            !isLoading && (
              <Card className="bg-slate-900 border-slate-800">
                <p className="text-sm text-slate-400">
                  Select a patient from the queue to see the pre-visit brief.
                </p>
              </Card>
            )
          )}

          {/* AI Pre-Visit Brief — top care gaps from HEDIS MeasureReports
              (TV-P1-05). Heuristic fallback when no MeasureReports exist. */}
          <AIPanel title="Pre-Visit Brief" subtitle="Care gap alerts and patient insights">
            <PreVisitBrief
              patientId={selectedRow?.patientId}
              gaps={careGaps}
              isLoading={isGapsLoading}
              source={careGapData?.source}
            />
          </AIPanel>
        </div>
      </div>
    </div>
  );
}

// ── Queue list states ──────────────────────────────────────────────────────

function QueueList({ isLoading, error, practitionerRef, queue, selectedId, onSelect, onRetry }) {
  if (!practitionerRef) {
    return (
      <EmptyState
        icon={<AlertCircle className="w-6 h-6" />}
        title="No practitioner in context"
        body="The SMART launch token didn't include an fhirUser claim. Re-launch from your EHR with a practitioner login."
      />
    );
  }
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mb-3" />
        <p className="text-xs">Loading today's queue…</p>
      </div>
    );
  }
  if (error) {
    return <FhirErrorState error={error} onRetry={onRetry} />;
  }
  if (queue.length === 0) {
    return (
      <EmptyState
        icon={<CheckCircle className="w-6 h-6 text-green-400" />}
        title="No visits scheduled today"
        body="Today's Appointment list came back empty for this practitioner."
      />
    );
  }

  return (
    <div className="divide-y divide-slate-800 max-h-[600px] overflow-y-auto">
      {queue.map((row) => (
        <div
          key={row.id}
          onClick={() => onSelect(row.id)}
          className={`p-4 cursor-pointer transition-all ${
            selectedId === row.id
              ? 'bg-slate-800 border-l-2 border-blue-500'
              : 'bg-slate-900 hover:bg-slate-800/50'
          }`}
        >
          <div className="flex items-start gap-3 mb-2">
            <TriageDot level={row.triageLevel} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Avatar name={row.name} size="sm" />
                <p className="font-medium text-white truncate">{row.name}</p>
                {row.live && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-500/15 border border-green-500/30"
                    title="Patient is in the waiting room"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-[10px] uppercase tracking-wider text-green-300 font-semibold">live</span>
                  </span>
                )}
                <IntakeBadge intakeProgress={row.intakeProgress} live={row.live} />
              </div>
              <p className="text-xs text-slate-400 mb-2">{row.chiefComplaint}</p>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Wait: {row.waitTime}m</span>
                <Badge
                  variant={row.queueBucket === 'completed' ? 'success' : row.queueBucket === 'in-visit' ? 'default' : 'warning'}
                  size="sm"
                >
                  {row.intakeStatus}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Intake progress badge (TV-P2-08) ──────────────────────────────────────
//
// Three checklist items from the patient waiting room (consent +
// questionnaire + A/V test). Renders only when there's a realtime
// presence on the row (`live=true`) — for FHIR-only rows the FHIR
// Appointment status badge still owns the row-card status slot.
//
// Display: `X/3` count + dotted tick row that fills as items land.
// Tooltip spells out which items are done so a quick hover answers
// "what's the patient still working through?" without expanding
// the row.
const INTAKE_KEYS = /** @type {const} */ (['consent', 'questionnaire', 'av']);
const INTAKE_LABELS = {
  consent: 'Consent',
  questionnaire: 'Questionnaire',
  av: 'Camera + mic test',
};

function IntakeBadge({ intakeProgress, live }) {
  if (!live || !intakeProgress) return null;
  const total = INTAKE_KEYS.length;
  const done = INTAKE_KEYS.filter((k) => !!intakeProgress[k]).length;
  const tone =
    done === total
      ? { wrap: 'bg-emerald-500/15 border-emerald-500/30', text: 'text-emerald-300' }
      : done > 0
        ? { wrap: 'bg-amber-500/15 border-amber-500/30', text: 'text-amber-300' }
        : { wrap: 'bg-slate-700/40 border-slate-600/50', text: 'text-slate-400' };
  const tooltipLines = INTAKE_KEYS.map(
    (k) => `${intakeProgress[k] ? '✓' : '○'} ${INTAKE_LABELS[k]}`,
  ).join('\n');
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border ${tone.wrap}`}
      title={`Intake ${done}/${total}\n${tooltipLines}`}
    >
      {INTAKE_KEYS.map((k) => (
        <span
          key={k}
          className={`w-1.5 h-1.5 rounded-full ${
            intakeProgress[k] ? 'bg-emerald-400' : 'bg-slate-600'
          }`}
        />
      ))}
      <span className={`text-[10px] font-mono ${tone.text}`}>{done}/{total}</span>
    </span>
  );
}

function EmptyState({ icon, title, body }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center text-slate-400">
      <div className="mb-3 text-slate-500">{icon}</div>
      <p className="text-sm font-medium text-white mb-1">{title}</p>
      <p className="text-xs text-slate-500">{body}</p>
    </div>
  );
}

// ── Patient preview card ───────────────────────────────────────────────────

/**
 * TV-RV-08 front-desk mode:
 *   canSeeClinical=false → hide Conditions / A1c blocks and Start Visit button;
 *   show operational action buttons instead (Check-In, Mark Arrived, No-Show,
 *   Assign-to-Provider).  These fire sandman /api/v1/scheduling PATCH endpoints
 *   in Slice 2's schedulingService; here we POST status updates through the
 *   existing FHIR appointment or a sandman convenience wrapper when available.
 *   For now the actions update local UI state + log to console so the surface is
 *   fully wired without requiring the sandman endpoint to be deployed first.
 */
function FrontDeskActions({ row }) {
  const [localStatus, setLocalStatus] = useState(row.queueBucket);
  const [assignee, setAssignee] = useState('');
  const [busy, setBusy] = useState(null); // which button is pending

  const act = async (action) => {
    setBusy(action);
    // In production these would call sandman PATCH /api/v1/scheduling/appointments/:id
    // with { status } or { assignedProviderId }.  Currently logs intent.
    console.info(`[FrontDesk] ${action} → appointmentId=${row.appointmentId} patientId=${row.patientId}`);
    // Optimistic local state update
    if (action === 'check-in' || action === 'arrived') setLocalStatus('in-visit');
    if (action === 'no-show') setLocalStatus('completed');
    await new Promise((r) => setTimeout(r, 400)); // simulated latency
    setBusy(null);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Operational Actions</p>

      {/* Status chips */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => act('check-in')}
          disabled={!!busy || localStatus === 'in-visit' || localStatus === 'completed'}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
        >
          {busy === 'check-in' ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3" />}
          Check-In
        </button>

        <button
          onClick={() => act('arrived')}
          disabled={!!busy || localStatus === 'completed'}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
        >
          {busy === 'arrived' ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogIn className="w-3 h-3" />}
          Mark Arrived
        </button>

        <button
          onClick={() => act('no-show')}
          disabled={!!busy || localStatus === 'completed'}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-700 hover:bg-red-800 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
        >
          {busy === 'no-show' ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserX className="w-3 h-3" />}
          No-Show
        </button>
      </div>

      {/* Assign-to-provider */}
      <div className="flex gap-2 mt-2">
        <input
          type="text"
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
          placeholder="Provider name or ID"
          className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
        />
        <button
          onClick={() => act('assign')}
          disabled={!!busy || !assignee.trim()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-purple-700 hover:bg-purple-800 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
        >
          {busy === 'assign' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Users className="w-3 h-3" />}
          Assign
        </button>
      </div>

      {/* Current status */}
      <div className="flex items-center gap-2 mt-1">
        <span className="text-xs text-slate-500">Status:</span>
        <Badge
          variant={localStatus === 'completed' ? 'success' : localStatus === 'in-visit' ? 'default' : 'warning'}
          size="sm"
        >
          {localStatus}
        </Badge>
      </div>
    </div>
  );
}

function PatientPreviewCard({ row, conditions, labs, isLoading, error, onStart, startPending, startError, canSeeClinical }) {
  // Highlight the most recent A1c if present in the lab list.
  const a1c = labs.find((l) => /hemoglobin a1c|hba1c/i.test(l.test));

  return (
    <Card className="bg-slate-900 border-slate-800">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <Avatar name={row.name} size="lg" />
          <div>
            <h3 className="text-xl font-semibold text-white">{row.name}</h3>
            <p className="text-sm text-slate-400">{row.chiefComplaint}</p>
            <p className="text-xs text-slate-500 mt-1">
              MRN {row.mrn}
              {row.age != null ? ` • Age ${row.age}` : ''}
            </p>
          </div>
        </div>
        <TriageDot level={row.triageLevel} />
      </div>

      {/* Clinical info — provider only (TV-RV-08: hidden from front-desk) */}
      {canSeeClinical && (
        <div className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b border-slate-800">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Conditions</p>
            {isLoading ? (
              <p className="text-xs text-slate-500 italic">Loading…</p>
            ) : error ? (
              <p className="text-xs text-red-400">Couldn't load conditions.</p>
            ) : conditions.length === 0 ? (
              <p className="text-xs text-slate-500 italic">None on file.</p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {conditions.slice(0, 6).map((c) => (
                  <Badge key={c.id} variant="secondary" size="sm">{c.name}</Badge>
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Latest A1c</p>
            {isLoading ? (
              <p className="text-xs text-slate-500 italic">Loading…</p>
            ) : a1c ? (
              <>
                <p className="text-white font-mono">
                  <span className="font-semibold">{a1c.value} {a1c.unit}</span>
                </p>
                <p className="text-xs text-slate-400">{a1c.date}</p>
              </>
            ) : (
              <p className="text-xs text-slate-500 italic">No recent A1c.</p>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Appointment</p>
          <p className="text-white text-sm">
            {row.start ? new Date(row.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Status</p>
          <Badge
            variant={row.queueBucket === 'completed' ? 'success' : row.queueBucket === 'in-visit' ? 'default' : 'warning'}
            size="sm"
          >
            {row.intakeStatus}
          </Badge>
        </div>
      </div>

      {/* TV-RV-08: front-desk gets operational buttons; provider gets Start Visit */}
      {canSeeClinical ? (
        <>
          {startError && (
            <div className="mb-3">
              <FhirErrorBanner error={startError} />
            </div>
          )}
          <button
            onClick={onStart}
            disabled={!row.patientId || startPending}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {startPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {startPending ? 'Starting Visit…' : 'Start Visit'}
          </button>
        </>
      ) : (
        <div className="border-t border-slate-800 pt-4">
          <FrontDeskActions row={row} />
        </div>
      )}
    </Card>
  );
}

// ── Pre-Visit Brief (top care gaps) ────────────────────────────────────────

function PreVisitBrief({ patientId, gaps, isLoading, source }) {
  if (!patientId) {
    return (
      <p className="text-sm text-slate-400">
        Select a patient from the queue to see care-gap alerts.
      </p>
    );
  }
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" />
        Scanning recent MeasureReports…
      </div>
    );
  }
  if (gaps.length === 0) {
    return (
      <p className="text-sm text-slate-400">
        No open care gaps for this patient.
      </p>
    );
  }
  // Show the highest-priority gaps first, cap at 3 for the brief.
  const sorted = [...gaps].sort((a, b) => {
    const rank = (g) => (g.status === 'overdue' ? 0 : g.status === 'due' ? 1 : 2);
    if (rank(a) !== rank(b)) return rank(a) - rank(b);
    return (a.priority === 'high' ? 0 : 1) - (b.priority === 'high' ? 0 : 1);
  });

  return (
    <div className="space-y-3">
      {source === 'heuristic' && (
        <p className="text-[11px] text-amber-400/80 italic">
          Condition-based recommendations — MeasureReport not available yet.
        </p>
      )}
      {sorted.slice(0, 3).map((gap) => (
        <div
          key={gap.id}
          className={`flex gap-2 p-3 rounded-lg ${
            gap.status === 'overdue'
              ? 'bg-red-500/10 border border-red-500/20'
              : 'bg-amber-500/10 border border-amber-500/20'
          }`}
        >
          <AlertCircle
            className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
              gap.status === 'overdue' ? 'text-red-400' : 'text-amber-400'
            }`}
          />
          <div className="flex-1">
            <p className="text-sm text-slate-200">{gap.gap}</p>
            {gap.lastDate && (
              <p className="text-xs text-slate-500">Last assessed: {gap.lastDate}</p>
            )}
          </div>
        </div>
      ))}
      {sorted.length > 3 && (
        <p className="text-[11px] text-slate-500">
          +{sorted.length - 3} more gap{sorted.length - 3 === 1 ? '' : 's'} in the patient's chart.
        </p>
      )}
    </div>
  );
}
